import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Card } from '../../../components/primitives/Card';
import { colors } from '../../../theme/colors';

interface LucideIconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

interface InsightCardProps {
  icon: ReactNode | ComponentType<LucideIconProps>;
  title: string;
  value: string;
  description: string;
  delay?: number;
  highlight?: boolean;
}

function isReactComponent(value: unknown): value is ComponentType<LucideIconProps> {
  if (typeof value === 'function') return true;
  if (typeof value === 'object' && value !== null) {
    const obj = value as { $$typeof?: symbol; render?: unknown };
    return typeof obj.render === 'function' || typeof obj.$$typeof === 'symbol';
  }
  return false;
}

export function InsightCard({
  icon,
  title,
  value,
  description,
  delay = 0,
  highlight = false,
}: Readonly<InsightCardProps>) {
  const [isHovered, setIsHovered] = useState(false);

  const glowColor = highlight ? colors.glow.cyan : colors.glow.purple;
  const borderColor = highlight ? colors.primary.neon : colors.accent.purple[500];

  const webHoverStyle = Platform.select({
    web: {
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      ...(isHovered && {
        boxShadow: `0 8px 24px ${glowColor}`,
        transform: [{ translateY: -3 }],
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const renderIcon = () => {
    if (isReactComponent(icon)) {
      const IconComponent = icon;
      return <IconComponent size={20} color={colors.primary.neon} strokeWidth={2} />;
    }
    return icon;
  };

  const cardStyle: ViewStyle = {
    flex: 1,
    width: '100%',
    alignSelf: 'stretch',
    minHeight: 170,
    ...(isHovered ? { borderColor } : null),
  };

  return (
    <FadeIn delay={delay} translateY={15} style={{ alignSelf: 'stretch', width: '100%' }}>
      <Card
        variant={highlight ? 'accent' : 'default'}
        padding="xl"
        radius="xl"
        style={[cardStyle, webHoverStyle]}
        {...hoverProps}
      >
        <VStack spacing="md" style={{ flex: 1, justifyContent: 'space-between' }}>
          <HStack spacing="md" align="center">
            <Box
              width={40}
              height={40}
              backgroundColor={highlight ? colors.primary.muted : colors.background.secondary}
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              style={{ borderRadius: 20 }}
            >
              {renderIcon()}
            </Box>
            <Text
              variant="caption"
              color={colors.text.tertiary}
              style={{
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontWeight: '600',
                fontSize: 11,
              }}
            >
              {title}
            </Text>
          </HStack>
          <VStack spacing="sm">
            <Text
              variant="h3"
              color={colors.text.primary}
              style={{ fontWeight: '700', letterSpacing: -0.5 }}
            >
              {value}
            </Text>
            <Text
              variant="bodySmall"
              color={colors.text.secondary}
              numberOfLines={2}
              style={{ lineHeight: 18 }}
            >
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card>
    </FadeIn>
  );
}
